import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Snackbar, Alert, Button, Box } from '@mui/material'
import { useVersionCheck } from '../hooks/useVersionCheck'

/**
 * Component to display a notification when the app has been updated
 * Shows a Material-UI snackbar with "Refresh Now" and "Later" actions
 * Reappears on navigation if dismissed and update is still available
 */
export function UpdateNotification() {
  const { hasUpdate, currentVersion, latestVersion } = useVersionCheck()
  const [open, setOpen] = useState(false)
  const [lastSeenVersion, setLastSeenVersion] = useState<string | null>(null)
  const location = useLocation()

  useEffect(() => {
    if (hasUpdate && latestVersion) {
      // Track the latest version we've seen
      if (lastSeenVersion !== latestVersion) {
        setLastSeenVersion(latestVersion)
      }

      // Show notification on every navigation when there's an update
      // This ensures users are reminded about updates when they navigate
      setOpen(true)
    }
  }, [hasUpdate, latestVersion, lastSeenVersion, location.pathname])

  const handleRefresh = () => {
    // Reload the page to get the new version
    window.location.reload()
  }

  const handleDismiss = () => {
    setOpen(false)
    // Note: We don't call dismissUpdateNotification() here anymore
    // because we want the notification to reappear on the next navigation
  }

  if (!hasUpdate) {
    return null
  }

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{
        marginTop: 2,
        '& .MuiAlert-root': {
          width: '100%',
        },
      }}
    >
      <Alert
        severity="info"
        variant="filled"
        onClose={handleDismiss}
        action={
          <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
            <Button
              color="inherit"
              size="small"
              onClick={handleRefresh}
              variant="outlined"
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.8)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Refresh Now
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={handleDismiss}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Later
            </Button>
          </Box>
        }
        sx={{
          fontSize: '0.95rem',
          alignItems: 'center',
          '& .MuiAlert-message': {
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiAlert-action': {
            paddingTop: 0,
            paddingBottom: 0,
            marginRight: 0,
          },
        }}
      >
        <Box>
          <strong>A new version of the app is available!</strong>
          <Box
            sx={{
              fontSize: '0.85rem',
              opacity: 0.9,
              marginTop: 0.5,
            }}
          >
            Current: {currentVersion} → Latest: {latestVersion}
          </Box>
        </Box>
      </Alert>
    </Snackbar>
  )
}
